/* eslint-disable max-len, react/no-did-update-set-state */
import React from 'react';
import PropTypes from 'prop-types';
import { mapObject, constant } from 'underscore';
import { withPerfMarker } from '@bingads-webui/performance-marker-react-wrapper';
import { Panel } from '@bingads-webui-campaign-react/panel';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ImageCropper } from '@bingads-webui-campaign-react/image-cropper';
import { getEditImageStyle } from '@bingads-webui-campaign/shared-image-experience-styles';

import { ImageExperienceImage } from '../model/image-experience-image';
import { Modal } from '../components/modal';
import { getPreview } from '../image-preview/preview-factory';
import { CarouselPreview133x100 } from '../image-preview/carousel-previews-133x100';
import { CarouselPreview178x100 } from '../image-preview/carousel-previews-178x100';
import { UnifiedPreviewGeneral } from '../image-preview/unified-preview-general';
import { UnifiedCarouselPreview133x100 } from '../image-preview/unified-carousel-previews-133x100';
import { UnifiedCarouselPreview178x100 } from '../image-preview/unified-carousel-previews-178x100';
import C from '../constants';

const defaultCropBoxHeight = 300;
const maxCropBoxWidth = 640;

class EditImageComponent extends React.PureComponent {
  constructor(props) {
    super(props);

    this.isMultimediaAd = this.props.isMultimediaAd;

    this.state = {
      image: props.image,
      aspectRatios: this.isMultimediaAd ?
        C.getMmaAspectRatios(props.imageOption) : C.aspectRatios,
    };
  }

  componentDidUpdate(prevProp) {
    if (prevProp.image !== this.props.image) {
      this.setState({
        image: this.props.image,
        aspectRatios: this.isMultimediaAd ?
          C.getMmaAspectRatios(this.props.imageOption) : C.aspectRatios,
      });
    }
  }

  onCropSettingsChanged = (cropSettings) => {
    // only setState when cropSettings actually changed.
    if (this.state.image &&
      JSON.stringify(this.state.image.CropSettings) === JSON.stringify(cropSettings)) {
      return;
    }

    const newImage = this.state.image.clone();

    newImage.setCropSettings({ ...cropSettings, CroppingType: 'ManualCrop' });

    this.setState({
      image: newImage,
    });
  };

  onSaveEditedImage = () => {
    this.props.onSave(this.state.image);
  };

  render() {
    const imageEditActions = () => (
      <div className="image-edit-actions">
        <button
          className="save-edit-button btn btn-primary"
          onClick={this.onSaveEditedImage}
        >
          {this.props.i18n.getString('Audience_Campaign_Save_Label')}
        </button>
        <button
          className="cancel-edit-button btn btn-default"
          onClick={this.props.onCancel}
        >
          {this.props.i18n.getString('Audience_Campaign_Cancel_Label')}
        </button>
      </div>
    );

    const imageCroppedInEdit = () => {
      let desiredBoxHeight = defaultCropBoxHeight;

      if (((desiredBoxHeight * this.state.image.Width) / this.state.image.Height) > maxCropBoxWidth) {
        desiredBoxHeight = Math.round((maxCropBoxWidth * this.state.image.Height) / this.state.image.Width);
      }

      return (
        <div className="image-cropper-in-edit" onContextMenu={(e) => { e.preventDefault(); return false; }}>
          <ImageCropper
            aspectRatio={this.state.aspectRatios[this.state.image.Type].aspectRatio}
            boxHeight={desiredBoxHeight}
            minSize={this.state.aspectRatios[this.state.image.Type].minSize}
            maxSize={[this.state.image.Width, this.state.image.Height]}
            imageSource={this.state.image.Src}
            cropSettings={this.state.image.CropSettings}
            onCropSettingsChanged={this.onCropSettingsChanged}
          />
          <div className="image-cropper-ratio-label">
            {this.props.i18n.getString(this.state.aspectRatios[this.state.image.Type].stringKey)}
          </div>
        </div>
      );
    };

    const editDialogTitle = () => (
      this.props.i18n.getString(
        this.props.imageOption === C.imageOptionKeys.Logo ?
          'ImageExperience_Edit_Logo_Template' : 'ImageExperience_Edit_Image_Template',
        {
          ratio: this.props.i18n.getString(this.state.aspectRatios[this.state.image.Type].stringKey),
        }
      )
    );

    const imageEditDescription = () => (
      <div className="image-preview-crop-description">
        {this.props.imageOption === C.imageOptionKeys.Logo ?
          this.props.i18n.getString('ImageExperience_Logo_ReCrop_Description') :
          this.props.i18n.getString('ImageExperience_Image_ReCrop_Description')}
      </div>
    );

    const modalDialogTitle = () => (
      <div className="edit-image-title">
        {editDialogTitle()}
      </div>);

    const modalDialogCancelButton = () => (
      <button
        className="close-dialog-button glyphicon glyphicon-remove"
        onClick={this.props.onCancel}
      />
    );

    const showUnifiedAdPreview = this.props.enableUnifiedAdPreview && this.props.adPreviewConfig;
    const CarouselPreview178x100Component = showUnifiedAdPreview ? UnifiedCarouselPreview178x100 : CarouselPreview178x100;
    const CarouselPreview133x100Component = showUnifiedAdPreview ? UnifiedCarouselPreview133x100 : CarouselPreview133x100;

    const editImageContent = () => (
      <div className="clearfix image-edit-content">
        {!this.props.isForVNext && modalDialogTitle()}
        {!this.props.isForVNext && modalDialogCancelButton()}
        {this.props.isForVNext && imageEditDescription()}
        <div className="clearfix edit-preview-section">
          <div className="edit-section" style={{ width: '50%', float: 'left' }}>
            {!this.props.isForVNext && imageEditDescription()}
            {imageCroppedInEdit()}
            {!this.props.isForVNext && imageEditActions()}
          </div>
          <div className="preview-section" style={{ width: '50%', float: 'left' }}>
            <div className="preview-section-title">
              {this.props.i18n.getString('ImageExperience_Ad_Preview_Title')}
            </div>
            {
              this.props.isImageAdExtensionMode && this.state.image.type === C.aspectRatios.Image133x100.type &&
              <CarouselPreview133x100Component
                i18n={this.props.i18n}
                previewTexts={this.props.previewTexts}
                image={this.state.image}
                genericImage={this.props.genericImage}
                placeHolderImages={this.props.placeHolderImages}
                classes={this.props.classes}
              />
            }
            {
              this.props.isImageAdExtensionMode && this.state.image.type === C.aspectRatios.Image178x100.type &&
              <CarouselPreview178x100Component
                i18n={this.props.i18n}
                previewTexts={this.props.previewTexts}
                image={this.state.image}
                classes={this.props.classes}
              />
            }
            {
              this.props.isImageAdExtensionMode &&
              this.state.image.type !== C.aspectRatios.Image133x100.type &&
              this.state.image.type !== C.aspectRatios.Image178x100.type &&
              !showUnifiedAdPreview &&
              getPreview(this.state.image, this.props.previewTexts, this.props.i18n, true, this.isMultimediaAd)
            }
            {
              !this.props.isImageAdExtensionMode && !showUnifiedAdPreview &&
              getPreview(this.state.image, this.props.previewTexts, this.props.i18n, true, this.isMultimediaAd)
            }
            {(
               (!this.props.isImageAdExtensionMode && showUnifiedAdPreview) ||
               (this.props.isImageAdExtensionMode &&
                this.state.image.type !== C.aspectRatios.Image133x100.type &&
                this.state.image.type !== C.aspectRatios.Image178x100.type &&
                showUnifiedAdPreview)
              ) &&
              <UnifiedPreviewGeneral
                {...this.props}
                image={this.state.image}
                adPreviewConfig={this.props.adPreviewConfig}
                previewContainerClassName={this.props.classes.previewPlacementContainerRoot}
              />
            }
          </div>
        </div>
        {this.props.isForVNext && imageEditActions()}
        <div className="alert alert-info">
          <div className="alert-image" />
          <span className="alert-info-inline">
            { this.props.i18n.getString('AdExtensions_Image_Preview_ClaimText') }
          </span>
        </div>
      </div>);

    const editImageContentVNext = () => (
      <div className="edit-image-content-container">
        {editImageContent()}
      </div>);

    if (this.props.isImageEditDisplayed && this.state.image && this.state.image.src) {
      return this.props.isForVNext ?
        <Panel
          title={editDialogTitle()}
          isOpen={this.props.isImageEditDisplayed}
          onClose={this.props.onCancel}
          getContainer="body"
          panelClassName={this.props.classes.editImageStyle}
          i18n={this.props.i18n}
          panelReadyAriaLabel={this.props.i18n.getString('Edit image side panel opened')}
        >
          {editImageContentVNext()}
        </Panel> :
        <Modal
          isModalOpen={this.props.isImageEditDisplayed}
          className="image-edit-modal"
        >
          {editImageContent()}
        </Modal>;
    }

    return null;
  }
}

EditImageComponent.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  onCancel: PropTypes.func.isRequired,
  previewTexts: PropTypes.shape({
    longHeadline: PropTypes.string.isRequired,
    shortHeadline: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    businessName: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }).isRequired,
  isImageEditDisplayed: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  image: PropTypes.instanceOf(ImageExperienceImage),
  isForVNext: PropTypes.bool,
  isImageAdExtensionMode: PropTypes.bool,
  isMultimediaAd: PropTypes.bool,
  genericImage: PropTypes.string,
  placeHolderImages: PropTypes.arrayOf(PropTypes.string),
  classes: PropTypes.shape(mapObject(
    getEditImageStyle({}, {}),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  imageOption: PropTypes.string,
  enableUnifiedAdPreview: PropTypes.bool,
};

EditImageComponent.defaultProps = {
  image: null,
  isForVNext: false,
  isImageAdExtensionMode: false,
  isMultimediaAd: false,
  genericImage: null,
  placeHolderImages: [],
  imageOption: C.defaultImageOption,
  enableUnifiedAdPreview: false,
};

const EditImage = withDefaultStyles(withPerfMarker(EditImageComponent), getEditImageStyle);

export { EditImage, EditImageComponent };
